import * as React from "react";
import { apiFetch } from "@/lib/api.js";

export interface Space {
  id: string;
  name: string;
  type: string;
  roomType: string | null;
  parentId: string | null;
  icon: string | null;
  description: string | null;
}

interface SpacesContextValue {
  spaces: Space[];
  loading: boolean;
  refetch: () => Promise<void>;
}

const SpacesContext = React.createContext<SpacesContextValue | null>(null);

export function SpacesProvider({ children }: { children: React.ReactNode }) {
  const [spaces, setSpaces] = React.useState<Space[]>([]);
  const [loading, setLoading] = React.useState(true);

  const refetch = React.useCallback(async () => {
    try {
      setSpaces(await apiFetch<Space[]>("/api/spaces"));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  React.useEffect(() => { refetch(); }, [refetch]);

  const value = React.useMemo(() => ({ spaces, loading, refetch }), [spaces, loading, refetch]);

  return <SpacesContext.Provider value={value}>{children}</SpacesContext.Provider>;
}

export function useSpaces(): SpacesContextValue {
  const ctx = React.useContext(SpacesContext);
  if (!ctx) throw new Error("useSpaces must be used within SpacesProvider");
  return ctx;
}
